import { useCallback } from 'react'

import { randUuid } from '@ngneat/falso'
import { usePersistentState } from 'react-persistent-state'

import type { SalesEditorTemplate } from '../state/types'

export const useEditorTemplates = () => {
  const [templates, setTemplates] = usePersistentState<SalesEditorTemplate[]>(
    [],
    {
      storageKey: 'sales-editor-templates',
    },
  )

  const createNewTemplate = useCallback(
    (name: string, data: SalesEditorTemplate['data'] = {}) => {
      if (!name) {
        throw new Error('Template name is required')
      }

      if (templates.some((template) => template.name === name)) {
        throw new Error('A template with this name already exists')
      }

      const newTemplate: SalesEditorTemplate = {
        id: randUuid(),
        name,
        data,
        isActive: false,
      }

      setTemplates((prevTemplates) => [...prevTemplates, newTemplate])

      return newTemplate
    },
    [templates, setTemplates],
  )

  const removeTemplate = useCallback(
    (id: string) => {
      setTemplates((prevTemplates) =>
        prevTemplates.filter((template) => template.id !== id),
      )
    },
    [setTemplates],
  )

  const updateTemplate = useCallback(
    (id: string, name?: string, data?: SalesEditorTemplate['data']) => {
      setTemplates((prevTemplates) =>
        prevTemplates.map((template) =>
          template.id === id
            ? {
                ...template,
                name: name ? name : template.name,
                data: data ? data : template.data,
              }
            : template,
        ),
      )
    },
    [setTemplates],
  )

  const changeActiveTemplate = useCallback(
    (id?: string) => {
      setTemplates((prevTemplates) =>
        prevTemplates.map((template) => ({
          ...template,
          isActive: template.id === id,
        })),
      )
    },
    [setTemplates],
  )

  const activeTemplate = templates.find((template) => template.isActive)

  return {
    templates,
    activeTemplate,
    createNewTemplate,
    removeTemplate,
    updateTemplate,
    changeActiveTemplate,
    setTemplates,
  }
}
